import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { getWebAuthRedirectUrl } from '@/lib/mobile/environment';

/**
 * Hook to handle magic link sign-in
 *
 * Sends a passwordless sign-in link to the user's email using our custom endpoint.
 * For security, always shows success message regardless of whether email exists.
 */
export function useMagicLinkSignIn() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('auth');

  const sendMagicLink = async (email: string) => {
    setIsLoading(true);

    try {
      const redirectUrl = getWebAuthRedirectUrl();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Call our custom edge function instead of default Supabase OTP
      const response = await fetch(`${supabaseUrl}/functions/v1/send-magic-link-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          redirectUrl,
        }),
      });

      if (!response.ok) {
        console.error('Magic link request failed with status:', response.status);
      }
    } catch {
      console.error('Unexpected error sending magic link');
    } finally {
      setIsLoading(false);
    }

    // Hide whether an account exists by responding with the same success toast
    // on every code path (HTTP error, fetch reject, real success).
    toast({
      title: t('magicLink.sentToastTitle'),
      description: t('magicLink.sentToastDescription'),
    });
    return { success: true };
  };

  return {
    isLoading,
    sendMagicLink,
  };
}
